import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gauge, Ruler, Calendar, ImageOff } from 'lucide-react';

const fmtPrice = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(v);
};

/**
 * Large teaser card for a "Druckmaschine" listing.
 * Blue accent. Emphasizes photo, manufacturer/model, year, operating hours, max width, price.
 */
export const MachineTeaserCard = ({ listing }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const photo = listing?.photos?.[0];
  const maker = [listing.manufacturer_name, listing.model_name].filter(Boolean).join(' · ');
  const year = listing.year_of_manufacture;
  const hours = listing.operating_hours;
  const maxWidth = listing?.specs?.max_width_mm;
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : 'en-US';

  return (
    <button
      type="button"
      onClick={() => navigate(`/listing/${listing.listing_id}`)}
      data-testid={`machine-teaser-${listing.listing_id}`}
      className="group text-left border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-500 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ease-out flex flex-col focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-950"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        {photo ? (
          <img
            src={photo}
            alt={listing.title}
            loading="lazy"
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-slate-300 dark:text-slate-600">
            <ImageOff className="h-10 w-10" />
          </div>
        )}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5">
          {t('machine.card.label')}
        </span>
      </div>
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          <h3 className="font-manrope text-sm md:text-base font-bold text-slate-900 dark:text-slate-50 leading-snug line-clamp-2">
            {listing.title}
          </h3>
          {maker && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1" data-testid="machine-maker">
              {maker}
            </p>
          )}
        </div>
        <dl className="grid grid-cols-3 gap-2 text-[11px] border-y border-slate-100 dark:border-slate-800 py-2">
          <div>
            <dt className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
              <Calendar className="h-3 w-3" /> {t('machine.card.year')}
            </dt>
            <dd className="font-mono font-semibold text-slate-900 dark:text-slate-100 tabular-nums">{year || '—'}</dd>
          </div>
          <div>
            <dt className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
              <Gauge className="h-3 w-3" /> {t('machine.card.hours')}
            </dt>
            <dd className="font-mono font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
              {hours ? new Intl.NumberFormat(locale).format(hours) : '—'}
            </dd>
          </div>
          <div>
            <dt className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
              <Ruler className="h-3 w-3" /> {t('machine.card.width')}
            </dt>
            <dd className="font-mono font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
              {maxWidth ? `${maxWidth}mm` : '—'}
            </dd>
          </div>
        </dl>
        <div className="flex items-baseline justify-between mt-auto">
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
            {t('machine.card.price')}
          </span>
          <span className="font-mono text-lg font-bold text-blue-700 dark:text-blue-400 tabular-nums">
            € {fmtPrice(listing.price_eur)}
          </span>
        </div>
      </div>
    </button>
  );
};
